import { useCallback, useEffect, useRef, useState } from 'react';
import type { AuthorizationParams } from '@auth0/auth0-react';

import {
  AUTH_SCOPE_READ_ONLY,
  tokenIsViewOnly,
  useActivityTimeout,
  useLastActivity,
  useViewOnlySync,
} from '@crossnokaye/ux-common';

/**
 * Manage view-only state and keep it in sync with the sister app (darkwing) via the
 * shared `ck.viewOnly` signal (see ux-common `useViewOnlySync`).
 *
 * - user toggles publish the change (cookie + broadcast)
 * - another app's change is reacted to by minting the matching token (no echo)
 * - on mount / refocus we reconcile from the shared cookie so a fresh tab honors a
 *   preference set elsewhere
 * - regaining write is a deliberate interactive re-login (mirrors darkwing): we clear the
 *   shared signal, force a fresh login, and the post-login reconcile lands on active
 *
 * @param isAuthenticated - Whether the user is authenticated
 * @param getAccessTokenSilently - Function to get the access token
 * @param loginAndReturn - Login redirect that regains write and returns to the current page
 * @returns {isViewOnly, requestViewOnlyToken, requestActiveToken}
 */
export const useViewOnlyState = (
  isAuthenticated: boolean,
  getAccessTokenSilently: (options?: any) => Promise<string>,
  loginAndReturn?: (authorizationParams?: AuthorizationParams) => Promise<void> | void
) => {
  const [isViewOnly, setIsViewOnly] = useState(false);

  // Mirror `isViewOnly` into a ref so the inactivity handler can read the current value without
  // being torn down and re-registered on every toggle.
  const isViewOnlyRef = useRef(isViewOnly);
  isViewOnlyRef.current = isViewOnly;

  // Serialize overlapping async token applications. Rapid cross-app signal flips fire `onChange`
  // repeatedly, each kicking off an independent, unordered token mint; without a guard a stale
  // mint could resolve last and clobber the newer cross-app state. Each apply claims the next
  // generation and only commits if it is still the latest.
  const applyGenerationRef = useRef(0);

  // The idle clock backing the inactivity watcher (same store `useActivityTimeout` reads), so we
  // can reset it when the tab regains active without a page reload.
  const { updateLastActivity } = useLastActivity();

  // Mint a read-only token (the Auth0 Action returns wrt:false for the custom read-only
  // `scopes`) and reflect it locally. No publish — this only applies state. Fails closed:
  // this path also backs the inactivity downgrade, so an error should land on view-only.
  // Returns whether this apply committed (still the latest generation) so callers can gate a
  // follow-up publish on it — a superseded apply must not broadcast stale state.
  const applyViewOnly = useCallback(async () => {
    const generation = ++applyGenerationRef.current;
    try {
      const token = await getAccessTokenSilently({
        cacheMode: 'off',
        // Auth0 bug: the event exposes a custom `scopes` prop, not `scope`.
        authorizationParams: { scopes: AUTH_SCOPE_READ_ONLY },
      });
      // Drop the result if a newer apply superseded this one while the mint was in flight.
      if (applyGenerationRef.current !== generation) return false;
      setIsViewOnly(tokenIsViewOnly(token));
      return true;
    } catch (error: any) {
      console.error('Failed to request view only token', error);
      if (applyGenerationRef.current !== generation) return false;
      setIsViewOnly(true);
      return true;
    }
  }, [getAccessTokenSilently]);

  // Mint a write token (no `scopes` → the Action grants write) to un-poison this origin's
  // cache and reflect the result locally. No publish. Fails open: on a transient error we
  // leave state untouched rather than lock an active user into view-only — write authority
  // is server-enforced via the wrt claim, and a genuine auth loss surfaces via re-login.
  const applyActive = useCallback(async () => {
    const generation = ++applyGenerationRef.current;
    try {
      const token = await getAccessTokenSilently({ cacheMode: 'off' });
      // Drop the result if a newer apply superseded this one while the mint was in flight.
      if (applyGenerationRef.current !== generation) return;
      setIsViewOnly(tokenIsViewOnly(token));
      // Regaining active — including silently from a sister app — counts as activity: reset the
      // idle clock so the re-arming inactivity watcher doesn't immediately downgrade on a stale
      // timestamp left over from before the tab went view-only.
      updateLastActivity(Date.now());
    } catch (error: any) {
      console.error('Failed to request active token', error);
    }
  }, [getAccessTokenSilently, updateLastActivity]);

  const { getViewOnly, publishViewOnly } = useViewOnlySync({
    onChange: (viewOnly) => {
      // Another tab/app changed the signal — apply state only, never re-publish (no echo).
      if (viewOnly) applyViewOnly();
      else applyActive();
    },
  });

  // User-initiated: go view-only and propagate to the sister app. Only broadcast if the apply
  // actually committed — a newer applyActive (e.g. a cross-app active signal) can supersede us
  // mid-mint, and publishing view-only then would desync the shared signal from our (now
  // active) local state.
  const requestViewOnlyToken = useCallback(async () => {
    const committed = await applyViewOnly();
    if (committed) publishViewOnly(true);
  }, [applyViewOnly, publishViewOnly]);

  // User-initiated: regain write via a fresh interactive login (mirrors darkwing — we
  // deliberately require re-auth on every view-only → active toggle). Clear the shared
  // signal first so the post-login reconcile lands on active and the sister app follows;
  // `loginAndReturn` carries the current page through the redirect. Await the redirect so the
  // caller's re-entrancy guard/spinner cover the pre-navigation gap (blocking a double-toggle),
  // and roll the signal back if the redirect never starts so a later reconcile doesn't mint
  // write off a login that never happened.
  const requestActiveToken = useCallback(async () => {
    publishViewOnly(false);
    try {
      await loginAndReturn?.({ prompt: 'login' });
    } catch (error: any) {
      console.error('Failed to start active re-login', error);
      publishViewOnly(true);
    }
  }, [publishViewOnly, loginAndReturn]);

  // Inactivity auto-downgrade: go view-only AND propagate to sister apps. Guarded so it only
  // fires the transition into view-only, never repeatedly while already view-only.
  const downgradeOnInactivity = useCallback(() => {
    if (isViewOnlyRef.current) return;
    requestViewOnlyToken();
  }, [requestViewOnlyToken]);

  // Re-arming (not one-shot): the lock must re-engage after every idle period. A one-shot only
  // re-arms on a full page reload, but a silent cross-app re-activation (applyActive) returns the
  // tab to active with no reload — which would leave the auto-downgrade permanently disarmed.
  useActivityTimeout({ onTimeout: downgradeOnInactivity, oneShot: false });

  // Reconcile from the shared cookie so a fresh load / refocus honors a preference set elsewhere.
  const reconcile = useCallback(async () => {
    if (getViewOnly()) {
      // Reflect the shared view-only signal immediately so a fresh load (e.g. a same-tab
      // navigation from a view-only sister app) paints view-only on the first authenticated
      // render instead of flashing "active" until the forced read-only mint below resolves.
      // applyViewOnly forces read-only scopes regardless, so this optimistic set can't disagree
      // with the mint's outcome — it just removes the network wait from the UI signal.
      setIsViewOnly(true);
      await applyViewOnly();
      return;
    }
    // Shared signal says active. A plain (cached) read is cheap, but read-only and write tokens
    // share one Auth0 cache slot (keyed by the default write scope — the read-only distinction
    // rides a custom `scopes` prop the cache can't see), so the cache can hand back a stale
    // read-only token that shadows real write authority. If it disagrees with the cookie, force a
    // fresh write mint (applyActive) to un-poison and confirm rather than trust the stale cache.
    try {
      const token = await getAccessTokenSilently();
      if (tokenIsViewOnly(token)) {
        await applyActive();
      } else {
        // Cached token already grants write — confirm active from it directly (no forced re-mint,
        // preserving the cheap-read fast path above). Mirror applyActive's idle-clock reset so the
        // re-arming inactivity watcher doesn't immediately downgrade on a stale timestamp.
        updateLastActivity(Date.now());
        setIsViewOnly(false);
      }
    } catch (error: any) {
      // Transient (e.g. silent-auth iframe race) — do not lock an active user into view-only.
      console.error('Failed to fetch token for view only check', error);
    }
  }, [getViewOnly, applyViewOnly, applyActive, getAccessTokenSilently, updateLastActivity]);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsViewOnly(false);
      return;
    }
    reconcile();
  }, [isAuthenticated, reconcile]);

  // Refocus / visibility reconcile (background tabs throttle the watcher's poll).
  useEffect(() => {
    if (!isAuthenticated) return;
    const onFocus = () => reconcile();
    const onVisible = () => {
      if (!document.hidden) reconcile();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAuthenticated, reconcile]);

  return { isViewOnly, requestViewOnlyToken, requestActiveToken };
};
